import type { MediaAssetRecord, ModuleVersionRecord } from "@contracts";
import { useDraggable } from "@dnd-kit/core";
import { IconFilter, IconGripVertical, IconPhotoPlus, IconSearch, IconUpload } from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import type { LibraryTab } from "./store";
import { categoryLabel, ModuleIcon, moduleSample } from "./ui";

function ModuleCard({ module, onAdd }: { module: ModuleVersionRecord; onAdd: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `module:${module.id}`, data: { type: "module", moduleId: module.id } });
  return <button
    ref={setNodeRef}
    className={`library-card${isDragging ? " dragging" : ""}`}
    onDoubleClick={onAdd}
    onClick={onAdd}
    {...listeners}
    {...attributes}
  >
    <span className="library-card-icon"><ModuleIcon module={module} size={23} /></span>
    <span className="library-card-copy"><strong>{module.name}</strong><small>{moduleSample(module)}</small></span>
    <IconGripVertical className="library-card-grip" size={17} />
  </button>;
}

function AssetCard({ asset, selected, onSelect }: { asset: MediaAssetRecord; selected: boolean; onSelect: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `asset:${asset.id}`, data: { type: "asset", assetId: asset.id } });
  return <button
    ref={setNodeRef}
    className={`asset-card${selected ? " selected" : ""}${isDragging ? " dragging" : ""}`}
    onClick={onSelect}
    {...listeners}
    {...attributes}
  >
    {asset.kind === "font" ? <span className="asset-font-preview">Aa</span> : <img src={asset.thumbnailUrl ?? asset.contentUrl} alt="" />}
    <span title={asset.name}>{asset.name}</span>
  </button>;
}

interface LibraryPanelProps {
  tab: LibraryTab;
  modules: ModuleVersionRecord[];
  assets: MediaAssetRecord[];
  selectedAssetId: string | null;
  onTab: (tab: LibraryTab) => void;
  onAddModule: (module: ModuleVersionRecord) => void;
  onSelectAsset: (asset: MediaAssetRecord) => void;
  onUpload: (file: File) => void;
}

export function LibraryPanel(props: LibraryPanelProps) {
  const [query, setQuery] = useState("");
  const [collection, setCollection] = useState("all");
  const fileRef = useRef<HTMLInputElement>(null);
  const categories = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = props.modules.filter((module) => !normalized || `${module.name} ${module.manifest.tags.join(" ")}`.toLowerCase().includes(normalized));
    return [...new Set(filtered.map((module) => module.category))].map((category) => ({ category, modules: filtered.filter((module) => module.category === category) }));
  }, [props.modules, query]);
  const collections = [...new Set(props.assets.map((asset) => asset.collectionId).filter(Boolean))];
  const filteredAssets = props.assets.filter((asset) => {
    const matchesQuery = !query || `${asset.name} ${asset.tags.join(" ")}`.toLowerCase().includes(query.toLowerCase());
    return matchesQuery && (collection === "all" || asset.collectionId === collection);
  });

  return <aside className="library-panel">
    <div className="library-tabs">
      <button className={props.tab === "modules" ? "active" : ""} onClick={() => props.onTab("modules")}>模块</button>
      <button className={props.tab === "assets" ? "active" : ""} onClick={() => props.onTab("assets")}>资源</button>
    </div>
    <div className="library-search-row">
      <label className="library-search"><IconSearch size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={props.tab === "modules" ? "搜索模块" : "搜索资源"} /></label>
      <button className="icon-button-dark" title="筛选"><IconFilter size={19} /></button>
    </div>
    {props.tab === "modules" ? <div className="library-scroll">
      {categories.map((group) => <section className="library-group" key={group.category}>
        <h3>{categoryLabel(group.category)}</h3>
        {group.modules.map((module) => <ModuleCard key={module.id} module={module} onAdd={() => props.onAddModule(module)} />)}
      </section>)}
    </div> : <div className="library-scroll assets-scroll">
      <div className="asset-actions">
        <button onClick={() => fileRef.current?.click()}><IconUpload size={17} />上传资产</button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.ttf,.otf,.woff,.woff2" hidden onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) props.onUpload(file);
          event.currentTarget.value = "";
        }} />
      </div>
      <label className="asset-filter"><span>集合</span><select value={collection} onChange={(event) => setCollection(event.target.value)}><option value="all">全部资源</option>{collections.map((id) => <option key={id!} value={id!}>{id === "collection_bender_weather_v6" ? "Bender Weather v6" : id}</option>)}</select></label>
      {filteredAssets.length > 0 ? <div className="asset-grid">{filteredAssets.map((asset) => <AssetCard key={asset.id} asset={asset} selected={asset.id === props.selectedAssetId} onSelect={() => props.onSelectAsset(asset)} />)}</div> : <div className="empty-library"><IconPhotoPlus size={30} /><span>没有匹配的资源</span></div>}
    </div>}
  </aside>;
}

export function DragPreview({ module, asset }: { module?: ModuleVersionRecord; asset?: MediaAssetRecord }) {
  if (module) return <div className="drag-preview-card"><span className="library-card-icon"><ModuleIcon module={module} size={23} /></span><span><strong>{module.name}</strong><small>{moduleSample(module)}</small></span></div>;
  if (asset) return <div className="drag-preview-card drag-preview-asset">{asset.kind === "font" ? <span className="asset-font-preview">Aa</span> : <img src={asset.thumbnailUrl ?? asset.contentUrl} alt="" />}<strong>{asset.name}</strong></div>;
  return null;
}
